// The Jata Negara header block shared by every Jadual Awalan export (PDF and
// Excel). THE one place to change it — the pages only say "put the header here".
//
// Crest only: the ministry lines under it are config-driven (jataTitleLines),
// not baked into the image the way the old JATA-BM.png composite had them.
import jataNegaraLogo from "@/assets/JATANEGARA.png";
import { jataTitleLines } from "@/services/surveyYear";

// Geometry measured off that old composite (4500 x 1694 px):
//   crest ink  y 252 .. 1155  → 904 px tall
//   title      cap-height 112 px, baselines at y 1346 and y 1508
// Everything scales by (rendered block height / SRC_H), so splitting the image
// into "crest + live text" leaves the exports looking exactly as before.
const SRC_H = 1694, CREST_TOP = 252, CREST_H = 904, CAP_H = 112, BASE1 = 1346, LEAD = 162;

let crestPromise = null;

// { dataUrl, width, height } for the crest — decoded once per page load and
// shared by both exports. A failed decode isn't cached, so a retry can succeed.
function crest() {
  if (!crestPromise) {
    crestPromise = new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext("2d").drawImage(img, 0, 0);
        let dataUrl;
        try { dataUrl = canvas.toDataURL("image/png"); } catch (e) { dataUrl = jataNegaraLogo; }
        resolve({ dataUrl, width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = err => { crestPromise = null; reject(err); };
      img.src = jataNegaraLogo;
    });
  }
  return crestPromise;
}

/* ---- PDF: crest + title lines, centred, filling the same 24mm block the old
        composite image did — so the report title still starts at y ≈ 35mm ---- */
export async function addJataHeaderPdf(doc, pageW, top = 5, blockMm = 24) {
  const img = await crest();
  const SC = blockMm / SRC_H;                  // mm per source px
  const h = CREST_H * SC;
  const w = img.width * (h / img.height);
  doc.addImage(img.dataUrl, "PNG", (pageW - w) / 2, top + CREST_TOP * SC, w, h);

  doc.setFont("helvetica", "normal");
  doc.setTextColor(0);
  // Helvetica cap-height is 0.717em, and 1mm = 72/25.4 pt.
  doc.setFontSize((CAP_H * SC / 0.717) * (72 / 25.4));
  (await jataTitleLines()).forEach((line, i) => {
    doc.text(line, pageW / 2, top + (BASE1 + i * LEAD) * SC, { align: "center" });
  });
}

/* ---- Excel: crest floated over row 1 with the title bottom-aligned beneath it.
        Deliberately ONE row — the title band (A2/A3) and every address below it
        keep their positions.
        `widths` is what was handed to ws.columns (numbers or { width }); it is
        only used to centre the floating image over the table.
        `lastCol` is the banner's last column letter, e.g. "N".            ---- */
const ROW1_PT = 74;                            // crest (53px) + 2 title lines
const EMU = 9525;                              // EMU per pixel
const colWidthPx = c => Math.floor((typeof c === "number" ? c : c.width) * 7 + 5);
// "N" -> 14, "AB" -> 28
const colIndex = letters => [...letters.toUpperCase()]
  .reduce((n, ch) => n * 26 + ch.charCodeAt(0) - 64, 0);

export async function addJataHeaderExcel(wb, ws, widths, lastCol) {
  const img = await crest();
  const SC = 100 / SRC_H;                      // px per source px — block was 100px tall
  const h = Math.round(CREST_H * SC);
  const w = Math.round(img.width * (h / img.height));

  ws.mergeCells(`A1:${lastCol}1`);
  ws.getRow(1).height = ROW1_PT;
  Object.assign(ws.getCell("A1"), {
    value: (await jataTitleLines()).join("\n"),
    alignment: { vertical: "bottom", horizontal: "center", wrapText: true },
    font: { name: "Arial", size: 7 },          // cap-height CAP_H * SC ≈ 6.9pt
  });

  // Centre the crest over the SAME span Excel centres the merged title over —
  // the banner, not every column in `widths` (a page may size columns past it).
  const banner = widths.slice(0, colIndex(lastCol));
  let left = Math.max(0, (banner.reduce((s, c) => s + colWidthPx(c), 0) - w) / 2);
  let col = 0;
  while (col < banner.length - 1 && left >= colWidthPx(banner[col])) {
    left -= colWidthPx(banner[col]); col++;
  }

  // Anchored in EMU, NOT as a fractional column: ExcelJS scales a fractional
  // tl.col by (width * 10000) EMU ≈ 1.05px per width-unit instead of the real
  // ~7px, which parks the image near a column boundary — visibly off-centre
  // from the title beneath it.
  ws.addImage(wb.addImage({ base64: img.dataUrl, extension: "png" }), {
    tl: {
      nativeCol: col, nativeColOff: Math.round(left * EMU),
      nativeRow: 0, nativeRowOff: Math.round(CREST_TOP * SC * EMU),
    },
    ext: { width: w, height: h },
  });
}
